import type { VenueOSClient, VenueOSContext } from '@mappedin/venueos-applet';
import { useEffect, useState } from 'react';
import type { Venue } from '../lib/map-helpers';

interface RawVenue {
  id: string;
  name?: string;
  latitude?: number | null;
  longitude?: number | null;
  slug?: string;
  city?: string;
  state?: string;
}

interface UseVenuesProps {
  client: VenueOSClient | null;
  context: VenueOSContext | null;
}

export interface UseVenuesReturn {
  venues: Venue[];
  isLoading: boolean;
  error: string | null;
}

function extractRawVenues(data: unknown): RawVenue[] {
  if (Array.isArray(data)) return data as RawVenue[];
  if (data && typeof data === 'object' && 'venues' in data) {
    const list = (data as { venues?: unknown }).venues;
    if (Array.isArray(list)) return list as RawVenue[];
  }
  return [];
}

function toVenue(raw: RawVenue): Venue | null {
  if (typeof raw.latitude !== 'number' || typeof raw.longitude !== 'number') return null;
  const venue: Venue = {
    id: raw.id,
    name: raw.name ?? raw.id,
    latitude: raw.latitude,
    longitude: raw.longitude,
  };
  if (raw.slug) venue.slug = raw.slug;
  if (raw.city) venue.city = raw.city;
  if (raw.state) venue.state = raw.state;
  return venue;
}

/**
 * Fetch the current org's venues. Replaces the App Builder's
 * `VenueOS.tools.call('search_venues', ...)` with a direct call to the
 * same `/api/organizations/:orgId/venues` route transit-app and
 * directory-applet use.
 */
export function useVenues({ client, context }: UseVenuesProps): UseVenuesReturn {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const orgId = context?.org.id ?? null;

  useEffect(() => {
    if (!client || !orgId) {
      setVenues([]);
      setIsLoading(client !== null);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const load = async (): Promise<void> => {
      try {
        const response = await client.api.fetch(`/api/organizations/${orgId}/venues`);
        if (cancelled) return;
        if (!response.ok) {
          setError(`Failed to fetch venues: ${response.status}`);
          setVenues([]);
          return;
        }
        const list = extractRawVenues(response.data)
          .map(toVenue)
          .filter((v): v is Venue => v !== null);
        setVenues(list);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to fetch venues');
        setVenues([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [client, orgId]);

  return { venues, isLoading, error };
}
